import React, {
  useEffect, Fragment, useState, useRef
}                                         from "react";
import { useSelector, useDispatch }       from "react-redux";

import PropTypes  from "prop-types";
import clsx       from "clsx";

import { lighten, makeStyles, styled }  from "@material-ui/core/styles";
import Grid                     from "@material-ui/core/Grid";
import Table                    from "@material-ui/core/Table";
import TableBody                from "@material-ui/core/TableBody";
import TableCell                from "@material-ui/core/TableCell";
import TableContainer           from "@material-ui/core/TableContainer";
import TableHead                from "@material-ui/core/TableHead";
import TablePagination          from "@material-ui/core/TablePagination";
import TableRow                 from "@material-ui/core/TableRow";
import TableSortLabel           from "@material-ui/core/TableSortLabel";
import Toolbar                  from "@material-ui/core/Toolbar";
import Typography               from "@material-ui/core/Typography";
import Paper                    from "@material-ui/core/Paper";
import TextField                from "@material-ui/core/TextField";
import Checkbox                 from "@material-ui/core/Checkbox";
import IconButton               from "@material-ui/core/IconButton";
import Tooltip                  from "@material-ui/core/Tooltip";
import MenuItem                 from "@material-ui/core/MenuItem";
import InputBase                from "@material-ui/core/InputBase";
import Link                     from "@material-ui/core/Link";
import AddBoxIcon               from "@material-ui/icons/AddBox";
import GetAppIcon               from "@material-ui/icons/GetApp";
import FilterIcon               from "@material-ui/icons/Filter";
import FilterNoneIcon           from "@material-ui/icons/FilterNone";
import UpdateIcon               from "@material-ui/icons/Update";
import Autocomplete             from "@material-ui/lab/Autocomplete";
import LinearProgress           from '@material-ui/core/LinearProgress';
import Popper                   from "@material-ui/core/Popper";
// import { tooltipClasses }       from "@mui/material/Tooltip";

import {
  getTableStickyParamsKeyNames, useProjectDataset,
  useStickyState, getRunConfigOption, showError,
  useTranslation, downloadData, jsonParse, mapObject,
  filterKeys,
}                                                     from "../../../core/utils";
import { DIALOG_USER_STATE }                          from "../../../core/constants";
import { getExpandedList, setExpandedList }           from "../../../features/annotationSettings";
import { getProjectId, getSelectedDataSetId }         from "../../../features/settings";
import { getFilter, setFilter, deleteFilter }         from "../../../features/filters";
import Location                                       from "../../../core/location";

import { TextFilter, FilterDialog }   from "./tableFilter";
import ConfirmDialog                  from "../../components/confirmDialog";

const LightTooltip = Tooltip; /* styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: '0.875rem',
  },
}));
*/

function descendingComparator(x, y, orderBy, order, headCells) {
  const key = a => {
    const _v = a[orderBy];
    const v = headCells.find(c => c._id == orderBy)?.viewData?.(_v) || _v;
    return v || v === 0 ? (typeof v == 'string' ? v.toLowerCase() : v) : undefined;
  };
  const a = key(x);
  const b = key(y);
  
  return b < a ? -1
      : b > a ? 1
          : a == undefined ? (b == undefined ? 0 : (order == "desc" ? 1 : -1))
              : b == undefined ? (order == "desc" ? -1 : 1) : 0;
};

function getComparator(order, orderBy, headCells) {
  return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy, order, headCells)
      : (a, b) => -descendingComparator(a, b, orderBy, order, headCells);
};

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
};

const getStyles = () => {
  const ptc = getRunConfigOption('projectTableColumns');
  return {
    tableCell: (id, cell) => ptc == 'show' ? undefined : jsonParse(ptc)?.__style?.[id]?.[cell._id],
  };
};

const border = (c, { style='solid', color='grey' } = {}) =>
  c.borderLeft ? { borderLeft: '1px '+ style +' '+ color } : {};

function EnhancedTableHead({
  id, headCells, rows, serverPagination, selectedDataSetId, projectId, order, orderBy,
  onRequestSort, isCheckBoxTableCell, onFilter: _onFilter, customBtns,
}) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();

  const mainHeadCells = headCells.filter(cell => !cell.unpackToRow || cell.unpackToRow.partial);

  const [searchesOpen, setSearchesOpen] = useState({});
  const [filterBy, setFilterBy] = useState(null);

  const reduxFilter = useSelector(getFilter(id));

  const getSelectedItems = () => {
    const obj = {};

    void headCells?.forEach(element => {
      const elementFilter = reduxFilter?.[element._id];

      obj[element._id] = { 
        checkedAll: elementFilter?.checkedAll || false,
        searchText: elementFilter?.searchText || "", 
        checked:    elementFilter?.checked    || [], 
        threshold:  elementFilter?.threshold  || ""
      }
    });
    return obj;
  }
  const selectedItems = getSelectedItems();

  const onFilter = filter => filter && (dispatch(setFilter({ id, filter })), _onFilter());

  const clearFiltering = () => {
    setFilterBy(null);

    if (reduxFilter) {
      dispatch(deleteFilter(id));
      _onFilter();
    }
  };

  useProjectDataset(id, type => {
    if (type == 'dataset')
      return;
    setSearchesOpen({});
    clearFiltering();
  },                                                          [projectId, selectedDataSetId]);

  /* disable clear filters on table reload (handleClickUpdateRow and/or new/updated rows)
  useEffect(() => { serverPagination || clearFiltering() },   [rows]);
  */
  
  const handleCloseFilterDialog = filter => {
    setFilterBy(null);
    onFilter(filter);
  };

  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  const isFilterOn = headcell =>
    reduxFilter?.[headcell] && (reduxFilter[headcell].checkedAll
                                || reduxFilter[headcell].checked?.length
                                || reduxFilter[headcell].threshold);

  const _TableSortLabel = headCell => {
    return headCell.sortable != false   // undefined or true
      ? (
        <TableSortLabel
          className={classes.columnName}
          active={orderBy === headCell._id}
          direction={orderBy === headCell._id ? order : "asc"}
          onClick={createSortHandler(headCell._id)}
        >
          {headCell.label}
          {orderBy === headCell._id ? (
            <span className={classes.visuallyHidden}>
              {order === "desc" ? "sorted descending" : "sorted ascending"}
            </span>
          ): null}
        </TableSortLabel>
      ) : typeof headCell.label == 'string' 
      ? (
        <span className={classes.columnName}>
          {headCell.label}
        </span>
      ): (
        <div className={classes.columnName}>
          {headCell.label}
        </div>
      );
  };

  const extractGroups = () => {
    if (!mainHeadCells.some(cell => cell.group_id))
      return null;
    const groups = [];
    const EMPTY = { name: '' };
    let pg = null;
    for (const cell of mainHeadCells) {
      const g = cell.group_id && { id: cell.group_id, name: cell.group_name } || EMPTY;
      if (!pg || g.id != pg.id) {
        pg = { ...g, span: 1, key: cell._id };
        groups.push(pg);
      } else
        pg.span ++;
    }
    return groups;
  };

  const groups = extractGroups();

  return (
    <TableHead>
      {filterBy && rows && (
        <FilterDialog 
          selectedItems={selectedItems}
          headCell={headCells.find(h => h._id == filterBy)}
          rows={rows}
          onClose={handleCloseFilterDialog}
      />)}
      {groups && (
        <TableRow style={{width: "100%", marginBottom: 10}}>
          {groups.map(g => (
            <TableCell
              // className={classes.tableCell +' '+ classes.tableHeadCell + classesSticky(headCell, classes)}
              key={g.key}
              align="center"
              colSpan={g.span}
            >
              {g.name}
            </TableCell>
          ))}
        </TableRow>
      )}
      <TableRow style={{width: "100%", marginBottom: 10}}>
        {isCheckBoxTableCell && <TableCell padding="checkbox" style={{width: "5%"}}/>}
        {mainHeadCells.map(headCell => (
          <TableCell
            className={classes.tableCell +' '+ classes.tableHeadCell + classesSticky(headCell, classes)}
            style={{width: headCell.width, whiteSpace: "nowrap", ...getStyles().tableCell(id, headCell), ...border(headCell)}}
            key={headCell._id}
            align="left"
            sortDirection={orderBy === headCell._id ? order : false}
          >
            {headCell.filterOn ?
              <Tooltip title={headCell.filterTooltip || t("common.filter_list")}>
                <IconButton size="small" onClick={() => setFilterBy(headCell._id)}>
                  {isFilterOn(headCell._id) ? <FilterIcon fontSize="small"/>: <FilterNoneIcon fontSize="small"/>}
                </IconButton>
              </Tooltip> : null}
            {headCell.customFilter || null}
            {headCell.textSearch &&
              <TextFilter
                filterBy={headCell._id}
                onToggleOpen={isOpen => setSearchesOpen({ ...searchesOpen, [headCell._id]: isOpen })}
                selectedItems={selectedItems}
                onFilter={onFilter}
                id={id}
                placeholder={headCell.text_label || headCell.label}
                serverPagination={serverPagination}
              />}
            {searchesOpen[headCell._id] ? null : headCell.title ?
              <Tooltip title={headCell.title}>
                {_TableSortLabel(headCell)}
              </Tooltip> : _TableSortLabel(headCell)}
          </TableCell>
        ))}
        {customBtns && <TableCell padding="checkbox"/>}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  id: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]),
  orderBy: PropTypes.string,
  headCells: PropTypes.array.isRequired,
};

const useToolbarStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
        color: theme.palette.secondary.main,
        backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  title: {
    flex: "1 1 100%",
    textAlign: "left",
  },
}));

const EnhancedTableToolbar = props => {
  const { toolBarName, newRowTitle, handleClickNewRow, handleClickUpdateRow, customHeaderButtons, downloadContent } = props;
  const { t } = useTranslation();
  const classes = useToolbarStyles();

  return (
    <Toolbar className={clsx(classes.root, {[classes.highlight]: false})}>
      <Typography className={classes.title} variant="h6" id="tableTitle">
        {toolBarName}
      </Typography>
      {customHeaderButtons && customHeaderButtons()}
      {downloadContent && <Tooltip title={"csv"}>
        <IconButton size="small" style={{marginLeft: 20}} onClick={downloadContent}>
          <GetAppIcon/>
        </IconButton>
      </Tooltip>}
      {handleClickNewRow && <Tooltip title={newRowTitle || t("common.new_row")}>
        <IconButton size="small" style={{marginLeft: 10}} onClick={handleClickNewRow}>
          <AddBoxIcon/>
        </IconButton>
      </Tooltip>}
      {handleClickUpdateRow && <Tooltip title={t("common.refresh")}>
        <IconButton size="small" style={{marginLeft: 10}} onClick={handleClickUpdateRow}>
          <UpdateIcon/>
        </IconButton>
      </Tooltip>}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  handleClickUpdateRow: PropTypes.func,
  handleClickNewRow: PropTypes.func,
};

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  combo: {
    "&:before": {
      borderBottom: 0,
    },
  },
  columnName: {
    marginLeft: 3,
  },
  outlinedInput: {
    '& label.Mui-focused': {
      borderWidth: 1,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderWidth: 0,
      },
      '&:hover fieldset': {
        borderWidth: 1,
      },
      '&.Mui-focused fieldset': {
        borderWidth: 1,
      },
    },
  },
  tableHeadCell: {
    padding: '5px 0 5px 7px !important', // important to override tableCell.paddingLeft FIXME: re-consider
  },
  tableCell: {
    maxWidth: 500,
    paddingLeft: 10,
  },
  underlinedInput: {
    '& .MuiInputBase-root:before': {
      borderBottom: 0,
    },
    '& .MuiInputBase-root .MuiAutocomplete-endAdornment': {
      display: 'none',
    },
    '& .MuiInputBase-root:hover .MuiAutocomplete-endAdornment': {
      display: 'initial',
    },
  },
  stickyCellRight: {
    position: 'sticky',
    right: 0,
    background: 'white',
    // boxShadow: '5px 5px 2px grey', - does not work for some unknown reason
  },
  actionButtonsContainer: {
    position: 'relative',
  },
  selectedRow: {
    background: '#efe',
  },
  actionButtons: {
     visibility: 'hidden',
    '*:hover > &': {
      visibility: 'visible',
    },
    position: 'absolute',
    top: 5,
    right: 0,
    background: 'white',
    display: 'block',
    width: 'fit-content !important',
    boxShadow: '0px 0px 5px 0px grey',
    borderRadius: 3,
  },
  pagination: {
    position: 'sticky',
    overflowY: 'hidden',
    bottom: -20,
    backgroundColor: '#fff',
    minHeight: 60,

    "& > div": {
      minHeight: 60,
    }
  }
}));

const classesSticky = (cell, classes) => cell.stick == 'right' ? ' '+ classes.stickyCellRight : '';

const useAutocompleteStyles = makeStyles({
  option: {
    fontSize: "0.875rem",
  },
});

export function viewLink(row, cell, _wrapper) {
  const style = { cursor: 'pointer', ...(cell.nohighlight ? {color: 'unset'} : {}), ...cell.style?.(row) };
  const wrapper = cell.viewData || _wrapper;
  return (
    <Link style={style} onClick={cell.link(row, cell)}>
      {(wrapper || (x => x))(row[cell._id]) ?? ''}
    </Link>
  );
}

export function viewText(row, cell) {
  const cell_value = row[cell._id] ?? "";
  const rowSpan = row.__spans?.[cell._id];
  const text_limit = cell.limit && ((rowSpan != -1 && rowSpan) ?? 2)*cell.limit/2;

  if (text_limit && cell_value.length > text_limit && !cell.unpackToRow)
    return <TrimText text={cell_value} limit={text_limit}/>

  return cell_value;
}

let said = {};

/* ex: runConfig.projectTableColumns = JSON.stringify({tests: ['!name', '!datasetname', 'trainset', 'filters'], __style:{tests:{model:{paddingLeft: 20}}}})
*/
const selectProjectTableColumns = id => {
  const data = getRunConfigOption('projectTableColumns');
  if (data == 'show') {
    console.log('TABLE:', id);
    return cell => { console.log('\tCELL:', cell._id); return true };
  }
  const descr = data && jsonParse(data)?.[id];
  const warn = (x,c) => {
    x && !said[c._id] && console.log(`////// Note: column '${c._id}' of table '${id}' is hidden!`);
    said[c._id] = x;
  }
  let x;
  return cell => cell.optional
    ? descr && descr.includes(cell._id)
    : !(descr && (x = descr.includes('!'+ cell._id), warn(x, cell), x));
};

/* const UE = useEffect; */

export default function EnhancedTable({
  id,
  open = true,
  headCells: _hcs,
  rows,
  viewCell = {},
  toolBarName,
  newRowTitle,
  tablePage = undefined,
  showPagination = true,
  localPaginationAbove = false,
  handleClickUpdateRow = undefined,
  handleClickNewRow = undefined,
  handleFilteredRows = undefined,
  customBtns = undefined,
  checkBoxTableCell,
  handleRowItemChanged,
  handleRowItemBlur = undefined,
  downloadCSVName = undefined,
  customHeaderButtons = undefined,
  stickyState = true,
  fetchRows,
  pagingListInfo,
  expandedRows,
  height = "100%",
  limitOptions,
  styles = {},
  fakeHeight,
  useLocationSearch,
  getMouseOverData,
}) {
  const headCells = _hcs.filter(selectProjectTableColumns(id));
  const serverPagination = Boolean(fetchRows);

  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();

  const projectId = useSelector(getProjectId);
  const selectedDataSetId = useSelector(getSelectedDataSetId);
  const isExpanded = useSelector(getExpandedList);
  const reduxFilter = useSelector(getFilter(id));

  const stickyParamsKeyNames = getTableStickyParamsKeyNames(projectId, selectedDataSetId, id);

  const [loading, setLoading] = useState(serverPagination);
  const [_order, setOrder] = useStickyState('rowNo:asc', stickyParamsKeyNames.order, stickyState);
  const order = (() => { const [by, dir] = _order?.split(':') || [undefined]; return { by, dir } })();

  const getQueryParams = useLocationSearch ? Location.getQueryParams : x => x;
  const [pageParams, _setPageParams] = useState(mapObject(getQueryParams({
    page: 1,
    limit: limitOptions?.[0] || 20,
  }), (v,k) => Number(v)-(k=='page'?1:0)));
  const setPageParams = params => {
    const { page, limit } = params;
    if (useLocationSearch)
      Location.setQueryParams({ page: page ? page + 1 : undefined, limit });
    _setPageParams(params);
  };
  const [filteredRows, setFilteredRows] = useState(rows);
  const [rowId, setRowId] = useState(null);
  const [selectedTextObj, setSelectedTextObj] = useState(null);
  const [mouseOver, setMouseOver] = useState();

  const [selectedRow, _setSelectedRow] = useState(Location.getQueryParams({ show: null }).show);
  const isRowSelected = id => id == selectedRow;
  const setSelectedRow = id => {
    Location.setQueryParams({ show: id });
    _setSelectedRow(id);
  };
  const toggleSelectRow = id => e =>
    e.ctrlKey
      ? setSelectedRow(selectedRow != id ? id : null)
      : selectedRow == id ? _setSelectedRow(null) : void 0; // deselect w/o updating query
  const showSelectedPage = () => {
    const i = rows.findIndex(r => r._id == selectedRow);
    if (i > 0)
      setPage(Math.floor(i/pageParams.limit));
  };

  const tableRef = useRef(null);
  const scrollToTop = () => tableRef.current?.scrollIntoView();

  const setPage = (page, force_reload) => {
    if (pageParams.page != page || force_reload)
      setPageParams({ ...pageParams, page });
  };

/*
  let N = 1;
  const useEffect = (f,d) => {
    const M = N++;
    UE(() => { console.log('['+ id +'] UE: '+ M, d); f() }, d);
  };
  console.log('['+ id +'] TTTTT',rows);
*/

  const _s = o => JSON.stringify(o);
  const _d = a => a && a.length && a;                                                     // empty arrays will all be equal in useEffect's deps comparison
  const sp = serverPagination;

  useProjectDataset(id, () => { setPage(0, true) },                 [projectId, selectedDataSetId]);

  useEffect(() => { selectedRow && showSelectedPage() },            []);
  useEffect(() => { tablePage && setPage(tablePage-1) },            [tablePage]);
  useEffect(() => { sp || applyFilters() },                         [sp || reduxFilter, sp || _d(rows)]);
  useEffect(() => { sp && getRows() },                              [sp && reduxFilter, sp && pageParams, sp && _order]);

  const handleRequestSort = (event, property) => {
    const prevDir = order.by == property ? order.dir : 'none';
    const dir = { none: 'asc', asc: 'desc', desc: 'none' }[prevDir];
    setOrder(dir == 'none' ? undefined : property +':'+ dir);
  };

  const getFiltersForAPIRequest = () => {
    let filters = [];
    if (reduxFilter)
      Object.keys(reduxFilter).forEach(key => {
        const f = reduxFilter[key];
        if (f.checked?.length)
          filters.push([key == "text" ? "entities.slotId" : key, "in", f.checked]);
        if (f.searchText)
          filters.push([key == "intentId" ? "IntentName" : key, "like", f.searchText]);
        else if (f.threshold)
          filters.push(["threshold", "from", f.threshold]);
      });
    return filters;
  };

  const applyFilters = () => {
    let newRows = rows;
    if (reduxFilter) {
      Object.keys(reduxFilter).forEach(key => {
        const f = reduxFilter[key];
        if (f.checked?.length)
          newRows = newRows.filter(r => f.checked.includes(r[key]._id || String(r[key])));
        if (f.searchText)
          try {
            newRows = newRows.filter(r => String(r[key]).match(new RegExp(f.searchText, 'i')));
          } catch (e) {
            console.log(`Warning: invalid search value: '${e}'`);
          }
      });
    }
    setFilteredRows(newRows);

    handleFilteredRows && handleFilteredRows(reduxFilter && newRows);
  };

  const getRows = () => {
    setLoading(true);

    const paging = {
      page: pageParams.page + 1,
      limit: pageParams.limit,
      dataset: selectedDataSetId,
      ...(order? {
      sort: { [order.by]: order.dir == "asc" ? 1 : -1 },
      } : {}),
      filters: getFiltersForAPIRequest(),
    };

    fetchRows(paging)
      .finally(() => setLoading(false));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    scrollToTop();
  };

  const handleChangeLimit = event =>
    setPageParams({ limit: Number(event.target.value), page: 0 });

  const onExpand = (_id, stillOpen) =>
    dispatch(setExpandedList({ _id, stillOpen }));

  const { page, limit } = pageParams;

  /* TODO: review */

  const __rows = serverPagination
    ? rows
    : (stableSort(filteredRows, getComparator(order.dir, order.by, headCells))
       .slice(page*limit, (page+1)*limit));
  const _rows = unpackCells(createRowSpans(__rows, headCells), headCells);

  function Pagination() {
    const curPage = pagingListInfo?.page ? pagingListInfo.page - 1 : page;
    const [pageBeforeEnter, setPageBeforeEnter] = useState(curPage + 1);

    const numRows = serverPagination ? pagingListInfo?.totalDocs || 0 : filteredRows.length;
    const totalPages = serverPagination
      ? pagingListInfo?.totalPages || 1
      : numRows > 0 ? Math.ceil(numRows/pageParams.limit) : 1;

    useEffect(() => {
      if (curPage > totalPages - 1 && !loading) {
        setPage(0);
        showError(dispatch, t)('common.error_missing_page', {
          page_num: curPage + 1,  page_size: numRows,  num_pages: totalPages
        });
      };
    }, [curPage, totalPages, loading])

    return showPagination ? (
        <TablePagination
          rowsPerPageOptions={limitOptions || [5, 10, 20, 40]}
          component="div"
          count={numRows}
          className={classes.pagination}
          rowsPerPage={limit}
          page={curPage}
          labelRowsPerPage={t("table.rows_per_page")}
          nextIconButtonText={t("table.next_page")}
          backIconButtonText={t("table.prev_page")}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeLimit}
          labelDisplayedRows={({ from, to, count }) => {
            return <>
              {`${from}-${to === -1 ? count : to} ${t("table.of")} ${count !== -1 ? count : to}`}
              <span style={{ marginLeft: 15, marginRight: 10 }}>{t("table.page")}:</span>
              <input
                style={{ border: "none", minWidth: 50, width: 19 + 7*String(totalPages).length }}
                onFocus={e => e.target.select()}
                type="number"
                onKeyPress={e => {
                  if (e.charCode == 13) {
                    handleChangePage(
                      e,
                      pageBeforeEnter && pageBeforeEnter - 1 >= 0 && pageBeforeEnter - 1 < totalPages?
                        pageBeforeEnter - 1:
                        curPage
                    );
                  }
                }}
                onBlur={() => {
                  setPageBeforeEnter(curPage + 1)
                }}
                onChange={e => {
                  const pageNum = e.target.value !== '0'? Number(e.target.value) || '': 0;

                  setPageBeforeEnter(pageNum)
                }}
                value={pageBeforeEnter}
              />
              {t("table.of")}
              <span style={{ marginLeft: 10 }}>{totalPages}</span>
            </>;
          }}
        />
    ) : null;
  }

  if (serverPagination) {
    if (handleClickUpdateRow) {
      console.error(String(new Error('No handleClickUpdateRow expected!')));
      return <p>Internal error! // see console</p>;
    }
    handleClickUpdateRow = () => {
      const PRESERVE_FILTERS = true; // disable clear filters on handleClickUpdateRow and/or new/updated rows
      if (PRESERVE_FILTERS) {
        getRows();
      } else {
        if (!reduxFilter)
          getRows();
        else
          dispatch(deleteFilter(id));
      }
    }
  }

  if (serverPagination && handleFilteredRows)
    return 'handleFilteredRows is not implemented for serverPagination mode';

  const downloadContent = downloadCSVName ? () => {
    const getCellData = row => cell => {
      const data = row[cell._id];
      return data && cell.comboData
      ? cell.comboData.find(d => d.value == data)?.label || '?'
      : data;
    };    
    downloadData(rows.map(row => headCells.map(getCellData(row)).join('\t')).join('\n'), downloadCSVName);
  } : null;

  return (
    <div className={classes.root} ref={tableRef} style={!open ? { display: 'none' }: styles.root}>
      <Paper className={classes.paper}>
        {toolBarName ?
          <EnhancedTableToolbar
            {...{
              toolBarName,
              newRowTitle,
              handleClickNewRow,
              handleClickUpdateRow,
              customHeaderButtons,
              downloadContent,
            }}
          />
          : null}
        {(getRunConfigOption('paginationAbove') || localPaginationAbove) && (rows.length / limit) > 1 && <Pagination/>}
        <TableContainer style={{height, overflowX: "auto", ...(styles.tableContainer || {})}}>
          <Table
            className={classes.table}
            size={"small"}
            style={fakeHeight ? {height: 0} : {}}
          >
            <EnhancedTableHead
              id={id}
              headCells={headCells}
              rows={rows}
              serverPagination={serverPagination}
              selectedDataSetId={selectedDataSetId}
              projectId={projectId}
              order={order.dir}
              orderBy={order.by}
              onRequestSort={handleRequestSort}
              isCheckBoxTableCell={!!checkBoxTableCell}
              onFilter={() => setPage(0)}
              customBtns={customBtns}
            />
            {!loading
              ? <TableBody>
                {_rows.map((row, index) => {
                  let key = `tablebody-fragment-${row._id}`;
                  if (row.__unpacked)
                    key += '-[unpacked]';
                  if (row.sub_id != undefined)
                    key += '-'+ row.sub_id;
                  return (
                    <Fragment key={key}>
                      <TableRow
                        className={classes.actionButtonsContainer +(isRowSelected(row._id) ? ' '+ classes.selectedRow : '')}
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row._id}
                        onClick={toggleSelectRow(row._id)}
                      >
                        {checkBoxTableCell && checkBoxTableCell(row._id, row.name, (pageParams.page*pageParams.limit) + index)}
                        {
                          headCells.filter(cell => row.__spans?.[cell._id] != -1)
                            .filter(cell => cell.unpackToRow?.partial || Boolean(cell.unpackToRow) == Boolean(row.__unpacked))
                            .map((cell, idx) => {
                              const props = {
                                ...(row.__spans?.[cell._id] && !row.__unpacked ? { rowSpan: row.__spans[cell._id] } : {}),
                                ...(cell.unpackToRow ? {
                                  colSpan: cell.unpackToRow.columns?.filter(() => true/* TODO is_col_shown*/).length ?? 1
                                } : {}),
                              };
                              return (
                            <TableCell
                              key={cell._id + idx} /* nice error w/o cell._id (!) */
                              {...props}
                              className={row?.__classes?.[cell._id] +' '+ classes.tableCell + classesSticky(cell, classes)}
                              align={cell.align}
                              style={{ wordWrap: "break-word", ...getStyles().tableCell(id, cell), ...border(cell) }}
                              onMouseMove={() => {
                                /* TODO: to minify re-render weight, setMouseOver should be local in a component 
                                 *       covering the group of rows corresponding the scope of highlighting, not all of them
                                 */
                                if (getMouseOverData?.[cell._id]) {
                                  const [row_id, cell_id] = [row._id, cell._id];
                                  if (index != mouseOver?.index || cell_id != mouseOver?.cell_id)
                                    setMouseOver({ index, row_id, cell_id, data: getMouseOverData[cell._id](row, cell) });
                                } else {
                                  if (mouseOver && index != mouseOver.index)
                                    setMouseOver(undefined);
                                }
                              }}
                            >
                              {(() => {
                                if (cell.unpackToRow?.partial && !Boolean(row.__unpacked))
                                  return '';

                                if (viewCell[cell._id])
                                  return viewCell[cell._id](row, cell, { setSelectedTextObj, mouseOver });

                                if (cell.customTextField || row.__customTextField?.[cell._id])
                                  return (
                                    <TrimText text={row[cell._id] ?? ''} limit={cell.limit}
                                      ctf={<CustomTextField
                                        id={cell._id + index}
                                        className={cell.classes?.customTextField}
                                        regularExpr={cell.regularExpr}
                                        value={row[cell._id]}
                                        multiline={cell.multiline}
                                        disabled={cell.disabled}
                                        onBlur={value => {
                                          handleRowItemChanged({ ...row, [cell._id]: value, rowName: cell._id });
                                        }}
                                      />}
                                    />
                                  );

                                const comboData = cell.comboData || row.__comboData?.[cell._id];
                                if (comboData) {
                                  if (row[cell._id] == null) // undefined/null OR loading value...
                                    return null;
                                  const wrong_value = { value: null, label: '[error: unknown]: '+ row[cell._id] };
                                  const value = comboData.find(d => d.value == row[cell._id]);
                                  const options = row[cell._id] && !value ? [...comboData, wrong_value] : comboData;

                                  const customPopper = cell.anotherPopper ? { PopperComponent: props => (
                                    <Popper {...props}
                                      style={{ width: "fit-content", backgroundColor: "#fff" }}
                                      placement='bottom-start'
                                    />
                                  )} : {};
                                  return (
                                    <Tooltip arrow title={value?.title || ""}>
                                      <Autocomplete 
                                        size="small"
                                        style={{ fontSize: "0.875rem", width: "100%" }}
                                        options={options}
                                        // filterOptions={options => options.filter(o => o != wrong_value)} - prevents completion
                                        getOptionLabel={option => option.label}
                                        disabled={typeof cell.disabled == "function" ? cell.disabled(row) : cell.disabled}
                                        autoComplete
                                        disableClearable
                                        autoHighlight
                                        fullWidth
                                        {...customPopper}
                                        renderOption={(option, state) =>
                                          <Typography selected={state.selected}>
                                            {option.title ? <Tooltip title={option.title || ""}>
                                              <span>{option.label}</span>
                                            </Tooltip> : option.label}
                                          </Typography>
                                        }
                                        value={row[cell._id] ? value || wrong_value : null}
                                        onChange={(event, value) => handleRowItemChanged({
                                          ...row,
                                          [cell._id]: value?.value || null,
                                          rowName: cell._id,
                                        })}
                                        renderInput={(params) => {
                                          const newParams = {
                                            ...params,
                                            InputProps: { ...params.InputProps, style: { fontSize: "0.875rem" } },
                                          };
                                          return <TextField
                                            className={classes.underlinedInput}
                                            {...newParams}
                                            variant="standard"
                                            id={cell._id + index}
                                            size="small"
                                            fullWidth
                                          />
                                        }}/>
                                    </Tooltip>
                                  );
                                }

                                if (cell.dateTime)
                                  return (
                                    <LightTooltip title={showDateTime(row[cell._id], 'time')} placement='right'>
                                      <span style={{cursor: 'arrow'}}>{showDateTime(row[cell._id])}</span>
                                    </LightTooltip>
                                  );

                                if (cell.checkBox) {
                                  return <Checkbox
                                    checked={row[cell._id]}  //it's controlled: rows changed after switch
                                    onChange={(event) => handleRowItemChanged(
                                      { ...row, [cell._id]: event.target.checked, rowName: cell._id })}
                                  />
                                }

                                if (cell.link)
                                  return viewLink(row, cell);

                                return viewText(row, cell);
                              })()}
                            </TableCell>
                          )})
                        }

                        {customBtns ? (
                          <TableCell padding="checkbox" align="right" className={'' && classes.actionButtons}>
                            <div>
                              {customBtns(row.name, row._id, row)}
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>

                      {expandedRows && isExpanded.find(id => id == row._id)
                          && expandedRows({
                            key: `${id}-subtable-${index}-${row._id}`,
                            row,
                            selectedTextObj: selectedTextObj?.rowId == row._id ? selectedTextObj : '',
                            refresh: getRows,
                          })}
                    </Fragment>
                  )})}
              </TableBody>
              :
              <TableBody>
                <TableRow>
                  <TableCell padding="checkbox" colSpan={7}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            }
          </Table>
        </TableContainer>
        <Pagination/>
      </Paper>
    </div>
  );
}

function unpackCells(rows, headCells) {
  if (!headCells)
    return rows;
  // only unpacking a single cell from a row is implemented now
  const ks = headCells.filter(c => c.unpackToRow).map(c => c._id);
  if (!ks.length)
    return rows;
  // Note: unpack is meant to work after span creation and only with spans not intersecting unpacked rows!
  return rows.map(r => ks.every(k => r.__spans?.[k] == -1) ? r : [{
    _id: r._id,
    sub_id: r.sub_id,
    __spans: r.__spans,
    ...Object.fromEntries(ks.map(k => [k, r[k]])),
    __unpacked: true,
  }, r]).flat();
}

function createRowSpans(rows, headCells) {
  if (!headCells)
    return rows;
  const spans = headCells.reduce((a, cell) => { a[cell._id] = null; return a }, {});
  const set_span = (row, k, size) => (row.__spans = (row.__spans || {}))[k] = size;
  return rows.concat({}).map(_row => {
    const row = {..._row};
    headCells.forEach(cell => {
      if (!cell.mergeEqualRows)
        return;
      const k = cell._id;
      const span = spans[k];
      if (!span) {
        spans[k] = { row, size: 1 };
      } else if (row[k] == span.row[k]) {
        set_span(row, k, -1);
        ++ span.size;
      } else  {
        if (span.size != 1)
          set_span(span.row, k, span.size);
        span.row = row;
        span.size = 1;
      }
    });
    return row;
  }).slice(0,-1);
}

function CustomTextField(props) {
  const { id, className, regularExpr, value: _v, multiline, disabled, onBlur } = props;

  const classes = useStyles();
  const [value, setValue] = useState(_v);

  useEffect(() => setValue(_v), [_v]);

  return <TextField 
    className={classes.outlinedInput + (className ? ' '+ className : '')}
    size="small"
    id={id}
    onChange={event => {
      let value = event.target.value;
      if (regularExpr)
        value = value.replace(regularExpr, "");
      setValue(value);
    }}
    onBlur={() => value != _v && onBlur(value)}
    value={value || ""}
    fullWidth
    multiline={Boolean(multiline)}
    disabled={Boolean(disabled)}
    variant="outlined"
    inputProps={{ style: { fontSize: "0.9rem" } }}
  />;
};

EnhancedTable.propTypes = {
  id: PropTypes.string.isRequired,
};

function TrimText(props) {
  const { text, limit, ctf } = props;
  // console.log('TTT', limit, text);
  const [show, setShow] = useState(false);
  const no_trim = !text || !limit || text.length <= limit + 20;
  const buttonStyle = {
    opacity: 0.4,  border: 'none',  fontWeight: 'bold',  cursor: 'pointer',
  };
  const buttonStyleLeft = { marginLeft: -5, marginRight: 5, ...buttonStyle };
  const buttonStyleRight = { marginLeft: 5,  ...buttonStyle };
  return no_trim ? ctf || text : ( // wrapped in span in case of block layout in the outer node
    <span>
      {ctf && show
        ? ctf 
        : <>
          {text.slice(0, limit)}
          {show && <span>{text.slice(limit)}</span>}
      </>}
      <button style={buttonStyleRight} onClick={() => setShow(!show)}>
        {show ? '<<' : '...'}
      </button>
    </span>
  )
}

function showRawDateTime(v) {
  return v && new Date(v).toLocaleString();
}

function showDateTime(v, only_time) {
  if (!v)
    return '';
  const getDate = d => d.toLocaleDateString(); // d.getFullYear() +'.'+ d.getMonth() +'.'+ d.getDate();
  const getTime = d => d.toLocaleTimeString(); // d.getHours() +':'+ d.getMinutes() +':'+ d.getSeconds();
  const now = new Date();
  const d = new Date(v);
  if (getDate(now) == getDate(d))
    return only_time ? '' : getTime(d);
  return only_time ? getTime(d) : getDate(d);
}
